import { z } from "zod";

/** A capability suggestion filed by an end user against one gateway. */
export type GatewaySuggestion = {
  id: string;
  gateway_id: string;
  gateway_name: string;
  content: string;
  /** User-source identity; admin-only (not exposed on MCP). */
  submitter_user_id: string;
  supporters: number;
  created_at: string;
  resolved_at: string | null;
  /** Set when resolved; optional even on resolve. */
  resolution_notes: string | null;
};

/**
 * Filters for the project-wide inbox. Repeated values within one filter match
 * any of them; different filters must all match.
 */
export type ProjectSuggestionListParams = {
  gateway_id?: string[];
  query?: string;
  suggestion_submitter?: string[];
  show_resolved?: boolean;
  limit?: number;
  offset?: number;
};

export type ResolveGatewaySuggestionRequest = {
  /** Optional note recorded with the resolution. */
  resolution_notes?: string;
};

const projectSuggestionSchema = z.object({
  id: z.string(),
  gateway_id: z.string(),
  gateway_name: z.string(),
  content: z.string(),
  submitter_user_id: z.string(),
  supporters: z.coerce.number(),
  created_at: z.string(),
  resolved_at: z.string().nullable(),
  resolution_notes: z.string().nullable().optional(),
});

export const projectSuggestionListResponseSchema = z.object({
  items: z.array(projectSuggestionSchema),
  total_count: z.coerce.number(),
});

export type ProjectSuggestionPage = {
  items: GatewaySuggestion[];
  totalCount: number;
};

export function normalizeProjectSuggestionList(
  data: z.infer<typeof projectSuggestionListResponseSchema>
): ProjectSuggestionPage {
  return {
    items: data.items.map((item) => ({
      id: item.id,
      gateway_id: item.gateway_id,
      gateway_name: item.gateway_name,
      content: item.content,
      submitter_user_id: item.submitter_user_id,
      supporters: item.supporters,
      created_at: item.created_at,
      resolved_at: item.resolved_at,
      resolution_notes: item.resolution_notes ?? null,
    })),
    totalCount: data.total_count,
  };
}
